/**
 * Cache Warmer Utility
 * Pre-fetches popular data to ensure fast response times
 * 
 * WHAT CACHE WARMING DOES:
 * ========================
 * Cache warming is like a restaurant preparing popular dishes before the lunch rush.
 * Instead of waiting for users to request data (causing them to wait for API calls),
 * we pre-fetch popular league fixtures and store them in the cache.
 * 
 * HOW IT WORKS:
 * 1. On dashboard load, it automatically fetches today's fixtures for popular leagues
 * 2. Fetches data for: Premier League, La Liga, Serie A, Bundesliga, MLS, etc.
 * 3. Stores this data in Firebase RTDB with appropriate TTLs
 * 4. When ANY user requests this data, it's instantly available (50ms vs 500ms+)
 * 
 * BENEFITS:
 * - First user of the day doesn't wait for API calls
 * - Popular leagues load instantly for everyone
 * - Reduces API calls by 95%+ for common requests
 * - Better user experience with instant data
 * 
 * EXAMPLE FLOW:
 * User opens dashboard → Cache warmer starts → Fetches top 10 leagues → 
 * Stores in RTDB → Next 1000 users get instant data from cache
 * 
 * CONSOLE OUTPUT EXPLAINED:
 * "Warmed fixtures for league 71" = Fetched & cached Brazilian Serie A fixtures
 * "Warmed fixtures for league 262" = Fetched & cached Liga MX fixtures  
 * "Cache warming complete" = All popular leagues are now cached and ready
 */

import rtdbFootballService from '../rtdbFootballService';
import { logCache } from '../rtdbConfig';

// Popular leagues that should always be cached
// Priority 1 = Cached first (most popular)
// Priority 2 = Cached second (regional favorites)
const PRIORITY_LEAGUES = [
  // European Top 5 - Most requested leagues globally
  { id: 39, name: 'Premier League', priority: 1 },        // England
  { id: 140, name: 'La Liga', priority: 1 },             // Spain
  { id: 78, name: 'Bundesliga', priority: 1 },           // Germany
  { id: 135, name: 'Serie A', priority: 1 },             // Italy
  { id: 61, name: 'Ligue 1', priority: 1 },              // France
  
  // Americas - High user base
  { id: 253, name: 'MLS', priority: 2 },                 // USA
  { id: 71, name: 'Brasileirao', priority: 2 },          // Brazil Serie A
  { id: 262, name: 'Liga MX', priority: 2 },             // Mexico
  
  // European Competitions
  { id: 2, name: 'Champions League', priority: 1 },      // UEFA CL
  { id: 3, name: 'Europa League', priority: 2 }          // UEFA EL
];

class CacheWarmer {
  constructor() {
    this.isWarming = false;
    this.lastWarmed = null;
    this.warmInterval = null;
  }

  /**
   * Warm cache for today's fixtures
   */
  async warmTodayFixtures() {
    const today = rtdbFootballService.getTodayDate();
    await this.warmFixturesForDate(today);
  }

  /**
   * Warm cache for specific date
   */
  async warmFixturesForDate(date) {
    if (this.isWarming) {
      logCache('info', 'Cache warming already in progress');
      return;
    }

    this.isWarming = true;
    logCache('info', `Starting cache warm for date: ${date}`);

    try {
      // Sort by priority
      const sortedLeagues = [...PRIORITY_LEAGUES].sort((a, b) => a.priority - b.priority);
      
      // Warm priority 1 leagues first
      const priority1 = sortedLeagues.filter(l => l.priority === 1);
      await this.warmLeagues(priority1, date);
      
      // Then warm priority 2 leagues
      const priority2 = sortedLeagues.filter(l => l.priority === 2);
      await this.warmLeagues(priority2, date);
      
      this.lastWarmed = new Date();
      logCache('info', 'Cache warming complete', { date, timestamp: this.lastWarmed });
    } catch (error) {
      logCache('error', 'Cache warming failed', { error: error.message });
    } finally {
      this.isWarming = false;
    }
  }

  /**
   * Warm specific leagues
   */
  async warmLeagues(leagues, date) {
    const warmPromises = leagues.map(league => 
      this.warmLeague(league.id, date)
        .catch(err => {
          logCache('error', `Failed to warm league ${league.name}`, { error: err.message });
          return null;
        })
    );

    await Promise.allSettled(warmPromises);
  }

  /**
   * Warm single league
   */
  async warmLeague(leagueId, date) {
    // For September 2025, all leagues use 2025
    // European leagues: 2025-2026 season = 2025 in API
    // Brazilian/MLS leagues: 2025 calendar year = 2025 in API
    const season = 2025;
    
    // Fetch fixtures
    await rtdbFootballService.getFixtures(
      null, null, null, date, leagueId, season
    );
    
    logCache('info', `Warmed fixtures for league ${leagueId}`, { date });
  }

  /**
   * Warm live fixtures
   */
  async warmLiveFixtures() {
    try {
      await rtdbFootballService.getFixtures(null, null, 'all');
      logCache('info', 'Warmed live fixtures');
    } catch (error) {
      logCache('error', 'Failed to warm live fixtures', { error: error.message });
    }
  }

  /**
   * Warm odds for specific fixtures
   */
  async warmOddsForFixtures(fixtureIds) {
    const oddsPromises = fixtureIds.slice(0, 10).map(fixtureId => 
      rtdbFootballService.getOdds(fixtureId)
        .catch(err => {
          logCache('error', `Failed to warm odds for fixture ${fixtureId}`, { error: err.message });
          return null;
        })
    );

    await Promise.allSettled(oddsPromises);
    logCache('info', `Warmed odds for ${fixtureIds.length} fixtures`);
  }

  /**
   * Start automatic cache warming
   */
  startAutoWarming(intervalMinutes = 5) {
    if (this.warmInterval) {
      clearInterval(this.warmInterval);
    }

    // Initial warm
    this.warmTodayFixtures();
    this.warmLiveFixtures();

    // Set up interval
    this.warmInterval = setInterval(() => {
      this.warmTodayFixtures();
      this.warmLiveFixtures();
    }, intervalMinutes * 60 * 1000);

    logCache('info', `Auto-warming started with ${intervalMinutes} minute interval`);
  }

  /**
   * Stop automatic cache warming
   */
  stopAutoWarming() {
    if (this.warmInterval) {
      clearInterval(this.warmInterval);
      this.warmInterval = null;
      logCache('info', 'Auto-warming stopped');
    }
  }

  /**
   * Get warming status
   */
  getStatus() {
    return {
      isWarming: this.isWarming,
      lastWarmed: this.lastWarmed,
      autoWarmingActive: !!this.warmInterval
    };
  }
}

// Create singleton instance
const cacheWarmer = new CacheWarmer();

// Export for browser console debugging
if (typeof window !== 'undefined') {
  window.RTDBCacheWarmer = {
    warmToday: () => cacheWarmer.warmTodayFixtures(),
    warmDate: (date) => cacheWarmer.warmFixturesForDate(date),
    warmLive: () => cacheWarmer.warmLiveFixtures(),
    startAuto: (minutes) => cacheWarmer.startAutoWarming(minutes),
    stopAuto: () => cacheWarmer.stopAutoWarming(),
    status: () => cacheWarmer.getStatus()
  };
}

export default cacheWarmer;